import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/ui/components/alert";
import { Button } from "@babackapp/ui/components/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@babackapp/ui/components/hover-card";
import { Separator } from "@babackapp/ui/components/separator";
import { ExternalLinkIcon } from "@radix-ui/react-icons";
import { WorkflowIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTemplatesEditorRouting } from "../context/templates-editor-routing-context";
import useTemplateWorkflowsDependencies from "../hooks/use-template-workflows-dependencies";

type DependantWorkflowsProps = {
  templateId?: string | null;
  /**
   * Trigger style:
   *  - "icon" (default): the orange workflow icon button shown in the templates
   *    list. Renders nothing when the template has no linked workflow.
   *  - "label": a compact "used by N workflow(s)" row for the editor sidebar,
   *    which replaces the previous full-height alert. Shows a muted "not used"
   *    line when the template has no linked workflow.
   */
  trigger?: "icon" | "label";
};

/**
 * Hover card listing the workflows that reference a given email template (v2:
 * many workflows can share one template). The same hover content is reused by
 * the templates list (orange icon) and the editor sidebar (compact label).
 */
export default function DependantWorkflows({
  templateId,
  trigger = "icon",
}: DependantWorkflowsProps) {
  const { t } = useTranslation("trackoffice");
  const dependantWorkflows = useTemplateWorkflowsDependencies(templateId);
  const { openWorkflow } = useTemplatesEditorRouting();

  if (!dependantWorkflows.length) {
    if (trigger === "label") {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <WorkflowIcon className="size-3.5" />
          {t(
            "templatesEditor.dependantWorkflows.notUsed",
            "Not used by any workflow",
          )}
        </span>
      );
    }
    return null;
  }

  const countLabel = t(
    "notificationsTemplatesEditor.emailEditor.header.templateUsageDescription",
    `This template is used for ${dependantWorkflows.length} workflow${dependantWorkflows.length > 1 ? "s" : ""}`,
    { count: dependantWorkflows.length },
  );

  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild>
        {trigger === "label" ? (
          <button
            type="button"
            className="inline-flex w-fit cursor-help items-center gap-1.5 text-xs font-medium text-orange-600 hover:underline"
          >
            <WorkflowIcon className="size-3.5" />
            {countLabel}
          </button>
        ) : (
          <Button
            variant="secondary"
            size="icon-xs"
            className="border border-orange-500 bg-transparent text-orange-500 shadow-none cursor-help"
          >
            <WorkflowIcon />
          </Button>
        )}
      </HoverCardTrigger>
      <HoverCardContent className="w-sm">
        <Alert variant="warning">
          <WorkflowIcon />
          <AlertTitle>
            {t(
              "templatesEditor.dependantWorkflows.title",
              "This template is active",
            )}
          </AlertTitle>
          <AlertDescription>
            {t(
              "templatesEditor.dependantWorkflows.description",
              "This template is currently used in one or more workflows. Be careful before deleting or editing it.",
            )}
          </AlertDescription>
        </Alert>
        <Separator className="my-4" />
        <div>
          {dependantWorkflows.map((workflow) => (
            <a
              key={workflow.id}
              className="flex cursor-pointer items-center gap-2 rounded-md p-2 hover:bg-accent"
              onClick={() => openWorkflow?.(workflow.id)}
            >
              <WorkflowIcon className="text-muted-foreground" />
              <div className="flex-1">
                <p className="text-xs font-medium">{workflow.name}</p>
                <p className="line-clamp-1 text-xs text-muted-foreground">
                  {workflow.description}
                </p>
              </div>
              <ExternalLinkIcon />
            </a>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
